import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { formatApiErrorDetail, friendlyError } from "../lib/api";
import { Lock, Mail, User2, Calendar, Instagram, ShieldCheck, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function RegisterPage() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [form, setForm] = useState({
    email: "",
    marketing_email: "",
    password: "",
    name: "",
    age: "",
    instagram_handle: "",
    gdpr_consent: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.gdpr_consent) {
      setError("Musíš potvrdit souhlas se zpracováním osobních údajů.");
      return;
    }
    setSubmitting(true);
    try {
      await register({
        email: form.email.trim().toLowerCase(),
        marketing_email: form.marketing_email.trim() ? form.marketing_email.trim().toLowerCase() : null,
        password: form.password,
        name: form.name.trim(),
        age: parseInt(form.age, 10),
        instagram_handle: form.instagram_handle.trim().replace(/^@/, ""),
        gdpr_consent: true,
      });
      toast.success("Účet vytvořen. Teď stačí ověřit občanku.");
      navigate("/verify");
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-paper py-10 px-4">
      <div className="max-w-xl mx-auto">
        <Link to="/" className="font-heading font-extrabold text-2xl tracking-tight text-slate-900 flex items-center gap-0.5 mb-8" data-testid="register-back-logo">
          ConnectYou<span className="text-sky-500">.</span>
        </Link>

        <div className="bg-white rounded-3xl border border-slate-200 p-6 sm:p-8 shadow-sm">
          <h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900">Vytvoř si účet</h1>
          <p className="text-slate-500 mt-1.5 font-medium">Pak ti stačí klik a letíš.</p>

          {/* Red security notice for primary email */}
          <div className="mt-6 rounded-2xl border border-red-200 bg-red-50 p-4 flex gap-3" data-testid="register-primary-email-notice">
            <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-bold text-red-700">Hlavní e-mail je pro důležité informace.</p>
              <p className="text-red-700/90 mt-0.5">Posíláme sem rezervace, jízdenky, změny u akcí a bezpečnostní upozornění. Použij e-mail, který opravdu čteš.</p>
            </div>
          </div>

          <form onSubmit={onSubmit} className="mt-5 flex flex-col gap-4">
            <Field icon={<Mail className="w-4 h-4" />} label="Hlavní e-mail (povinný)" testid="register-email">
              <input
                data-testid="register-email-input"
                type="email" required value={form.email}
                onChange={(e) => set("email", e.target.value)}
                placeholder="ty@email.cz"
                className="input-style"
              />
            </Field>

            <Field icon={<Mail className="w-4 h-4" />} label="E-mail pro nabídky (volitelný)" testid="register-marketing">
              <input
                data-testid="register-marketing-input"
                type="email" value={form.marketing_email}
                onChange={(e) => set("marketing_email", e.target.value)}
                placeholder="můžeš zadat jiný, pokud chceš dostávat nabídky šité na míru"
                className="input-style"
              />
              <p className="text-xs text-slate-500 mt-1.5 font-medium">Sem ti budeme posílat tipy na akce, slevy a tematické nabídky.</p>
            </Field>

            <Field icon={<Lock className="w-4 h-4" />} label="Heslo (min. 8 znaků)" testid="register-password">
              <input
                data-testid="register-password-input"
                type="password" required minLength={8} value={form.password}
                onChange={(e) => set("password", e.target.value)}
                className="input-style"
              />
            </Field>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field icon={<User2 className="w-4 h-4" />} label="Jméno a příjmení" testid="register-name">
                <input
                  data-testid="register-name-input"
                  type="text" required minLength={2} value={form.name}
                  onChange={(e) => set("name", e.target.value)}
                  placeholder="Jan Novák"
                  className="input-style"
                />
              </Field>
              <Field icon={<Calendar className="w-4 h-4" />} label="Věk" testid="register-age">
                <input
                  data-testid="register-age-input"
                  type="number" required min={15} max={120} value={form.age}
                  onChange={(e) => set("age", e.target.value)}
                  className="input-style"
                />
              </Field>
            </div>

            <Field icon={<Instagram className="w-4 h-4" />} label="Instagram (kvůli chatu a propojení)" testid="register-ig">
              <input
                data-testid="register-ig-input"
                type="text" required value={form.instagram_handle}
                onChange={(e) => set("instagram_handle", e.target.value)}
                placeholder="napiš svůj IG, např. pepa.123"
                className="input-style"
              />
            </Field>

            {/* Data protection box */}
            <div className="rounded-2xl bg-sky-50 border border-sky-100 p-4 flex gap-3" data-testid="register-data-protection-notice">
              <ShieldCheck className="w-5 h-5 text-sky-600 shrink-0 mt-0.5" />
              <div className="text-sm text-sky-900">
                <p className="font-bold">Tvoje údaje jsou v bezpečí.</p>
                <p className="mt-0.5">Skryté údaje (e-mail, heslo, věk, občanka) jsou šifrované a má k nim přístup výhradně tým ConnectYou. Jakákoliv manipulace s nimi je protizákonná a může vést k trestnímu stíhání.</p>
              </div>
            </div>

            {/* GDPR */}
            <label className="flex gap-3 items-start cursor-pointer select-none">
              <input
                data-testid="register-gdpr-checkbox"
                type="checkbox" checked={form.gdpr_consent}
                onChange={(e) => set("gdpr_consent", e.target.checked)}
                className="mt-1 w-5 h-5 accent-sky-500"
              />
              <span className="text-sm text-slate-700 font-medium">
                Souhlasím se zpracováním osobních údajů za účelem bezpečnosti komunity, prevence nevhodného chování a vedení záznamů pro případ porušení pravidel nebo zákona. Beru na vědomí, že údaje budou uchovávány v souladu s GDPR.
              </span>
            </label>

            {error && (
              <div data-testid="register-error" className="rounded-xl bg-red-50 text-red-700 text-sm font-semibold p-3">{error}</div>
            )}

            <button
              data-testid="register-submit-btn"
              type="submit" disabled={submitting}
              className="mt-2 px-6 py-3 rounded-full bg-sky-500 hover:bg-sky-600 disabled:opacity-60 text-white font-bold transition-all active:scale-95 shadow-[0_12px_30px_-10px_rgba(14,165,233,0.55)]"
            >
              {submitting ? "Vytvářím účet…" : "Pokračovat na ověření občanky"}
            </button>

            <p className="text-center text-sm text-slate-500 mt-2">
              Už máš účet?{" "}
              <Link data-testid="register-go-login" to="/login" className="text-sky-600 font-bold underline underline-offset-4">Přihlas se</Link>
            </p>
          </form>
        </div>
      </div>

      <style>{`
        .input-style { width:100%; padding:0.75rem 1rem; border-radius:0.75rem; background:#fff; border:1px solid #E5E7EB; font-weight:500; font-size:15px; outline:none; transition:all .2s; }
        .input-style:focus { border-color:#0EA5E9; box-shadow:0 0 0 4px rgba(14,165,233,0.15); }
      `}</style>
    </div>
  );
}

function Field({ icon, label, children, testid }) {
  return (
    <div data-testid={`${testid}-field`}>
      <label className="flex items-center gap-1.5 text-sm font-bold text-slate-700 mb-1.5">
        <span className="text-slate-400">{icon}</span>
        {label}
      </label>
      {children}
    </div>
  );
}
